import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import Styles from './TimeForFirstChargeCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import {
  ChartComponent,
  IFText,
  IFSkeleton,
  IFSegmentedControl,
} from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { useTranslation } from 'react-i18next'
import { useApexResizeObserver } from 'Utils/Resizeobserver'
function isColorDark(hex) {
  const c = hex.startsWith('#') ? hex.substring(1) : hex
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b
  return luminance < 140
}
const TimeForFirstChargeCard = ({
  timeForFirstCharge,
  fetchTimeForFirstCharge,
  fetchTimeForFirstChargeRequestState,
  filter,
}) => {
  const chartWrapperRef = useApexResizeObserver()
  const { t } = useTranslation()
  const [segmentedState, setSegmentedState] = useState(t('Analytics.Charge'))

  useEffect(() => {
    fetchTimeForFirstCharge(filter)
  }, [])

  useEffect(() => {
    fetchTimeForFirstCharge(filter)
  }, [filter])

  const { seriesCharge, optionsCharge, seriesMoney, optionsMoney, hasNoData } =
    useMemo(() => {
      const breakdownCharge =
        timeForFirstCharge?.data?.intervalBreakdownCharge ?? []
      const seriesCharge = breakdownCharge.map((i) => Number(i.count) || 0)

      const breakdownMoney =
        timeForFirstCharge?.data?.intervalBreakdownAddMoney ?? []
      const seriesMoney = breakdownMoney.map((i) => Number(i.count) || 0)

      const labels = breakdownCharge.map((i) => i.label)

      const hasNoData =
        (!seriesCharge.length || seriesCharge.every((v) => Number(v) === 0)) &&
        (!seriesMoney.length || seriesMoney.every((v) => Number(v) === 0))
      const sliceColors = [
        Colors.Analytics.PeakHour.Shade1,
        Colors.Analytics.PeakHour.Shade2,
        Colors.Analytics.PeakHour.Shade3,
        Colors.Analytics.PeakHour.Shade4,
        Colors.Analytics.PeakHour.Shade5,
        Colors.Analytics.PeakHour.Shade6,
      ]

      const labelColors = sliceColors.map((c) =>
        isColorDark(c) ? '#fff' : Colors.black,
      )

      const optionsCharge = {
        chart: {
          type: 'pie',
        },
        labels,
        legend: {
          position: 'bottom',
        },
        dataLabels: {
          style: {
            colors: labelColors,
          },
          enabled: true,
          formatter: (val, opts) => {
            const item = breakdownCharge?.[opts?.seriesIndex]
            if (!item) return ''
            return `${val.toFixed(1)}%`
          },
        },
        tooltip: {
          style: {
            fontColor: labelColors,
          },
          y: {
            formatter: (val, opts) => {
              const item = breakdownCharge?.[opts?.seriesIndex]
              if (!item) return val
              return `${item.count} (${item.percentage.toFixed(1)}%)`
            },
          },
        },
        stroke: { show: false },
        states: {
          hover: {
            filter: {
              type: 'none',
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        colors: sliceColors,
      }

      const optionsMoney = {
        chart: {
          type: 'pie',
        },
        labels,
        legend: {
          position: 'bottom',
        },
        dataLabels: {
          style: {
            colors: labelColors,
          },
          enabled: true,
          formatter: (val, opts) => {
            const item = breakdownMoney?.[opts?.seriesIndex]
            if (!item) return ''
            return `${val.toFixed(1)}%`
          },
        },
        tooltip: {
          style: {
            fontColor: labelColors,
          },
          y: {
            formatter: (val, opts) => {
              const item = breakdownMoney?.[opts?.seriesIndex]
              if (!item) return val
              return `${item.count} (${item.percentage.toFixed(1)}%)`
            },
          },
        },
        stroke: { show: false },
        states: {
          hover: {
            filter: {
              type: 'none',
            },
          },
          active: {
            filter: {
              type: 'none',
            },
          },
        },
        colors: sliceColors,
      }

      return {
        seriesCharge,
        optionsCharge,
        seriesMoney,
        optionsMoney,
        hasNoData,
      }
    }, [timeForFirstCharge])
  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      <div className={Styles.GraphContainer}>
        <div className={Styles.Segment}>
          <IFSegmentedControl
            name="group-1"
            isLoading={
              fetchTimeForFirstChargeRequestState === RequestState.LOADING
            }
            callback={(val) => {
              setSegmentedState(val)
            }}
            controlRef={useRef()}
            segments={[
              {
                label: t('Analytics.Charge'),
                value: t('Analytics.Charge'),
                ref: useRef(),
              },
              {
                label: t('Analytics.AddMoney'),
                value: t('Analytics.AddMoney'),
                ref: useRef(),
              },
            ]}
          />
        </div>

        {fetchTimeForFirstChargeRequestState === RequestState.LOADING ? (
          <div className={Styles.ChartSkeleton}>
            <IFSkeleton width="100%" height="100%" variant="rectangular" />
          </div>
        ) : (
          <div ref={chartWrapperRef} className={Styles.Chart}>
            <ChartComponent
              options={
                segmentedState === t('Analytics.Charge')
                  ? optionsCharge
                  : optionsMoney
              }
              series={
                segmentedState === t('Analytics.Charge')
                  ? seriesCharge
                  : seriesMoney
              }
              height="100%"
              width="100%"
              type="pie"
              className={hasNoData ? Styles.OverlayTest : Styles.PieChart}
            />
            {hasNoData && (
              <IFText
                className={Styles.OverlayText}
                style={{ color: Colors.red }}
              >
                {t('Analytics.NoData')}
              </IFText>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  timeForFirstCharge: AnalyticsSelectors.getTimeForFirstCharge(state),
  fetchTimeForFirstChargeRequestState:
    AnalyticsSelectors.getTimeForFirstChargeRequestState(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchTimeForFirstCharge: (filter) =>
      dispatch(AnalyticsActions.fetchTimeForFirstCharge(filter)),
  }
}

TimeForFirstChargeCard.propTypes = {
  filter: PropTypes.object,
  fetchTimeForFirstCharge: PropTypes.func,
  fetchTimeForFirstChargeRequestState: PropTypes.number,
  timeForFirstCharge: PropTypes.object,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TimeForFirstChargeCard)
